import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";
import { X, ArrowRight, ArrowLeft, Check, Loader2, Sparkles, UserPlus, Activity, Send } from "lucide-react";

function getToken() {
  return localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token");
}

const SYMPTOM_OPTIONS = [
  { key: "fatigue",       bg: "Умора",              en: "Fatigue" },
  { key: "weight_gain",   bg: "Наддаване на тегло",  en: "Weight gain" },
  { key: "insomnia",      bg: "Безсъние",            en: "Insomnia" },
  { key: "brain_fog",     bg: "Мозъчна мъгла",       en: "Brain fog" },
  { key: "anxiety",       bg: "Тревожност",          en: "Anxiety" },
  { key: "low_libido",    bg: "Ниско либидо",        en: "Low libido" },
  { key: "joint_pain",    bg: "Ставна болка",        en: "Joint pain" },
  { key: "skin_issues",   bg: "Кожни проблеми",      en: "Skin issues" },
  { key: "digestive",     bg: "Храносмилателни",     en: "Digestive" },
  { key: "headaches",     bg: "Главоболия",          en: "Headaches" },
];

/** Create Patient Wizard — 4 steps: Basic Info → Quick Input → Analysis → Transfer. */
export default function CreatePatientWizard({ open, onClose, onCreated }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [step, setStep] = useState(0);
  const [patient, setPatient] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(false);

  // Step 1 form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("female");

  // Step 2 form
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [symptoms, setSymptoms] = useState([]);
  const [labsText, setLabsText] = useState("");
  const [diagnosis, setDiagnosis] = useState("");

  if (!open) return null;

  const overlay = `fixed inset-0 z-[100] flex items-center justify-center p-4
    ${isDark ? "bg-black/70" : "bg-black/50"} backdrop-blur-sm`;
  const panel = `relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl
    ${isDark ? "bg-[hsl(222,47%,10%)] text-white" : "bg-white text-gray-900"}`;

  const steps = [
    { title_bg: "Основни данни",    title_en: "Basic Info",   icon: UserPlus },
    { title_bg: "Клинични данни",   title_en: "Clinical Data", icon: Activity },
    { title_bg: "AI анализ",        title_en: "AI Analysis",   icon: Sparkles },
    { title_bg: "Прехвърли достъп", title_en: "Transfer Access", icon: Send },
  ];

  const createPatient = async () => {
    if (!name || !age) {
      toast.error(bg ? "Попълнете всички задължителни полета" : "Fill all required fields");
      return;
    }
    setLoading(true);
    try {
      const token = getToken();
      const { data } = await axios.post(`${API}/doctor/patients`,
        { name, email: email || null, age: Number(age), gender },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setPatient(data);
      toast.success(bg ? "Пациентът е създаден" : "Patient created");
      setStep(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Error"));
    } finally { setLoading(false); }
  };

  const submitQuickInput = async () => {
    setLoading(true);
    try {
      const token = getToken();
      await axios.post(`${API}/doctor/patients/${patient.id}/quick-input`,
        {
          weight: weight ? Number(weight) : null,
          height: height ? Number(height) : null,
          symptoms: symptoms.length ? symptoms : null,
          labs_text: labsText || null,
          diagnosis: diagnosis || null,
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(bg ? "Данните са запазени" : "Data saved");
      setStep(2);
    } catch {
      toast.error(bg ? "Грешка при запис" : "Save error");
    } finally { setLoading(false); }
  };

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const { data } = await axios.post(`${API}/doctor/patients/${patient.id}/analyze`, {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setAnalysis(data);
      toast.success(bg ? "AI анализът е готов" : "AI analysis ready");
    } catch {
      toast.error(bg ? "Грешка при AI анализа" : "AI analysis error");
    } finally { setLoading(false); }
  };

  const sendInvite = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const { data } = await axios.post(`${API}/doctor/patients/${patient.id}/invite`, {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setInvite(data);
      try { await navigator.clipboard?.writeText(data.activation_link); } catch {}
      toast.success(bg ? "Линкът е копиран" : "Link copied");
    } catch {
      toast.error(bg ? "Грешка при поканата" : "Invitation error");
    } finally { setLoading(false); }
  };

  const finish = () => {
    onCreated?.(patient);
  };

  const inputCls = `w-full px-3 py-2 text-sm rounded-lg border outline-none
    ${isDark ? "bg-[hsl(222,47%,8%)] border-white/10 text-white placeholder:text-gray-500"
             : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400"}`;
  const labelCls = `block text-xs font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`;

  return (
    <div className={overlay} onClick={onClose} data-testid="create-patient-wizard">
      <div className={panel} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`px-6 py-4 border-b flex items-center justify-between
          ${isDark ? "border-white/5" : "border-gray-200"}`}>
          <div>
            <h2 className="text-lg font-bold">
              {bg ? "Създаване на пациент" : "Create Patient"}
            </h2>
            <p className={`text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {bg ? steps[step].title_bg : steps[step].title_en} · {step + 1}/{steps.length}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg ${isDark ? "hover:bg-white/5 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
            data-testid="wizard-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress */}
        <div className="px-6 py-3 flex items-center gap-1">
          {steps.map((s, i) => (
            <div
              key={s.id || s.title || `step-${i}`}
              className={`flex-1 h-1.5 rounded-full transition-colors
                ${i <= step ? "bg-gradient-to-r from-violet-500 to-fuchsia-600" : (isDark ? "bg-white/5" : "bg-gray-200")}`}
            />
          ))}
        </div>

        {/* Body */}
        <div className="px-6 pb-6 pt-2">
          {step === 0 && (
            <div className="space-y-3" data-testid="wizard-step-1">
              <div>
                <label className={labelCls}>{bg ? "Име или инициали *" : "Name or initials *"}</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="patient-name-input" />
              </div>
              <div>
                <label className={labelCls}>{bg ? "Имейл (препоръчително)" : "Email (recommended)"}</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="patient@example.com" className={inputCls} data-testid="patient-email-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{bg ? "Възраст *" : "Age *"}</label>
                  <input type="number" value={age} onChange={(e) => setAge(e.target.value)} className={inputCls} data-testid="patient-age-input" />
                </div>
                <div>
                  <label className={labelCls}>{bg ? "Пол *" : "Gender *"}</label>
                  <select value={gender} onChange={(e) => setGender(e.target.value)} className={inputCls} data-testid="patient-gender-select">
                    <option value="female">{bg ? "Жена" : "Female"}</option>
                    <option value="male">{bg ? "Мъж" : "Male"}</option>
                    <option value="other">{bg ? "Друго" : "Other"}</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3" data-testid="wizard-step-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{bg ? "Тегло (kg)" : "Weight (kg)"}</label>
                  <input type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} className={inputCls} data-testid="weight-input" />
                </div>
                <div>
                  <label className={labelCls}>{bg ? "Височина (cm)" : "Height (cm)"}</label>
                  <input type="number" value={height} onChange={(e) => setHeight(e.target.value)} className={inputCls} data-testid="height-input" />
                </div>
              </div>
              <div>
                <label className={labelCls}>{bg ? "Симптоми (множествен избор)" : "Symptoms (multi-select)"}</label>
                <div className="flex flex-wrap gap-1.5">
                  {SYMPTOM_OPTIONS.map(s => {
                    const active = symptoms.includes(s.key);
                    return (
                      <button
                        key={s.key}
                        onClick={() => setSymptoms(active ? symptoms.filter(x => x !== s.key) : [...symptoms, s.key])}
                        className={`text-[11px] px-2.5 py-1 rounded-full border transition-all
                          ${active
                            ? "bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white border-transparent"
                            : (isDark ? "border-white/10 text-gray-300 hover:bg-white/5" : "border-gray-200 text-gray-700 hover:bg-gray-50")}`}
                        data-testid={`symptom-${s.key}`}
                      >
                        {bg ? s.bg : s.en}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className={labelCls}>{bg ? "Диагноза (по избор)" : "Diagnosis (optional)"}</label>
                <input value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} placeholder="ICD-10 или свободен текст" className={inputCls} data-testid="diagnosis-input" />
              </div>
              <div>
                <label className={labelCls}>{bg ? "Лабораторни (paste текст – AI парсва)" : "Labs (paste text — AI parsed)"}</label>
                <textarea
                  value={labsText}
                  onChange={(e) => setLabsText(e.target.value)}
                  rows={3}
                  placeholder={bg ? "Glucose: 5.4, Insulin: 7.2, HbA1c: 5.5, TSH: 2.1, Vitamin D: 28..."
                                : "Glucose: 5.4, Insulin: 7.2, HbA1c: 5.5, TSH: 2.1, Vitamin D: 28..."}
                  className={inputCls}
                  data-testid="labs-input"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3" data-testid="wizard-step-3">
              {!analysis ? (
                <div className="text-center py-6">
                  <Sparkles className="w-10 h-10 mx-auto mb-3 text-violet-500" />
                  <p className={`text-sm mb-4 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    {bg ? "Готови ли сте да генерирате клиничен AI анализ?"
                        : "Ready to generate a clinical AI analysis?"}
                  </p>
                  <button
                    onClick={runAnalysis}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600
                      text-white text-sm font-semibold hover:opacity-95 disabled:opacity-60"
                    data-testid="run-analysis-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {bg ? "Генерирай анализ" : "Generate analysis"}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className={`text-sm leading-relaxed p-3 rounded-lg
                    ${isDark ? "bg-[hsl(222,47%,8%)]" : "bg-gray-50"}`}>
                    {bg ? analysis.summary_bg : analysis.summary_en}
                  </p>
                  {analysis.top_recommendations?.length > 0 && (
                    <ul className="space-y-1 text-sm">
                      {analysis.top_recommendations.map((r, i) => {
                        const txt = typeof r === "string" ? r : (bg ? r.bg : r.en);
                        return (
                          <li key={`rec-${txt}-${i}`} className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span>{txt}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3" data-testid="wizard-step-4">
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                {bg ? "Изпратете линк за активация на пациента. При натискане, той задава парола и получава 30-дневен пълен достъп."
                    : "Send an activation link to the patient. They set a password and receive 30 days of full access."}
              </p>
              {!invite ? (
                <button
                  onClick={sendInvite}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600
                    text-white text-sm font-semibold hover:opacity-95 disabled:opacity-60"
                  data-testid="send-invite-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {bg ? "Генерирай линк и изпрати" : "Generate link & send"}
                </button>
              ) : (
                <div className={`rounded-lg p-3 ${isDark ? "bg-emerald-500/10 border border-emerald-900/30" : "bg-emerald-50 border border-emerald-200"}`}>
                  <p className={`text-xs font-semibold mb-1 ${isDark ? "text-emerald-300" : "text-emerald-800"}`}>
                    {bg ? "Линк за активация:" : "Activation link:"}
                  </p>
                  <p className={`text-[11px] break-all font-mono ${isDark ? "text-emerald-200" : "text-emerald-900"}`}
                    data-testid="activation-link">
                    {invite.activation_link}
                  </p>
                  {invite.email_sent && (
                    <p className={`text-[10px] mt-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      ✓ {bg ? "Имейл е изпратен" : "Email sent"}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className={`px-6 py-4 border-t flex items-center justify-between
          ${isDark ? "border-white/5 bg-[hsl(222,47%,8%)]" : "border-gray-200 bg-gray-50"}`}>
          <button
            onClick={step > 0 ? () => setStep(step - 1) : onClose}
            className={`inline-flex items-center gap-1 px-3 h-9 rounded-lg text-xs font-semibold
              ${isDark ? "text-gray-300 hover:bg-white/5" : "text-gray-700 hover:bg-gray-200"}`}
            data-testid="wizard-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {step > 0 ? (bg ? "Назад" : "Back") : (bg ? "Отказ" : "Cancel")}
          </button>

          {step === 0 && (
            <button
              onClick={createPatient}
              disabled={loading || !name || !age}
              className="inline-flex items-center gap-1 px-4 h-9 rounded-lg text-xs font-semibold
                bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-95 disabled:opacity-50"
              data-testid="wizard-next"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>{bg ? "Напред" : "Next"}<ArrowRight className="w-3.5 h-3.5" /></>}
            </button>
          )}
          {step === 1 && (
            <button
              onClick={submitQuickInput}
              disabled={loading}
              className="inline-flex items-center gap-1 px-4 h-9 rounded-lg text-xs font-semibold
                bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-95 disabled:opacity-50"
              data-testid="wizard-next"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>{bg ? "Напред" : "Next"}<ArrowRight className="w-3.5 h-3.5" /></>}
            </button>
          )}
          {step === 2 && (
            <button
              onClick={() => setStep(3)}
              disabled={loading}
              className="inline-flex items-center gap-1 px-4 h-9 rounded-lg text-xs font-semibold
                bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-95 disabled:opacity-50"
              data-testid="wizard-next"
            >
              {bg ? "Напред" : "Next"}<ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
          {step === 3 && (
            <button
              onClick={finish}
              className="inline-flex items-center gap-1 px-4 h-9 rounded-lg text-xs font-semibold
                bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:opacity-95"
              data-testid="wizard-finish"
            >
              <Check className="w-3.5 h-3.5" />
              {bg ? "Готово" : "Done"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
